// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormattedMessage } from 'react-intl';
import {
  LoadingContentAvatar,
  LoadingContentStripe,
} from '../../../../../elements/LoadingContentFigures/LoadingContentFigures';
import { CommaFormat } from '../../../../../elements/Price/Price';
import { RadioButtonLoading } from '../../../../../elements/RadioButton/RadioButton';
import type {BagItem, ShoppingCartItem} from '../../../../../entities';
import {Desktop, Mobile, Tablet} from '../../../../../utils/responsive';
import {isIphone13} from '../../../../../utils/string';
import VideoModal from '../../../../home/components/Discover/VideoModal';
import RadioButton from '../CustomRadioButton/RadioButton';
import DIYCourier from '../DIYCourier/DIYCourier';

import style from './ImportMethod.scss';
import icons from '../../../../../assets/styles/icons.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import loadingStripeStyle from '../../../../../elements/LoadingContentFigures/LoadingContentFigures.scss';
import full from '../../../../../assets/img/new-order/quote-step/Full.png';
import lite from '../../../../../assets/img/new-order/quote-step/Lite.png';

import type { Forecast, ForecastCourier, ForecastTier, ForecastType } from '../../api';

type Props = {
  forecast?: Forecast;
  isLoading?: boolean;
  handleChangeOption: (option: ForecastType) => void;
  importMethod?: ForecastType;
  preferredImportationMethod?: string;
  items: (ShoppingCartItem | BagItem)[];
};

type State = {
  modalOpen: {
    DIY: boolean;
    CLASSIC: boolean;
  };
};

const importVideoUrl = {
  DIY: 'https://www.youtube.com/embed/clbl4hmftA0?rel=&controls=0&showinfo=0&autoplay=1',
  CLASSIC: 'https://www.youtube.com/embed/H4ba7AyBiC8?rel=&controls=0&showinfo=0&autoplay=1',
}

const ImportMethodOptionLoading = () =>
    <div className={classNames(style.option, style.skeleton)}>
      <div className={style.radioWrap}>
        <RadioButton checked={false}
                     name="importMethod"/>
      </div>
      <div className={style.mainContent}>
        <div className={style.leftWrap}>
          <div className={style.titleWrapResponsive}>
            <div className={style.titleWrap}>
              <LoadingContentAvatar width={56} colorClassName={loadingStripeStyle.black100} />
              <div className={style.textWrap}>
                <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                      width={95}
                                      margin="8px 0 8px 0"/>
                <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                      width={152}
                                      margin="0 0 8px 0"/>
              </div>
            </div>
          </div>
          <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                width={240}
                                margin="16px 0 8px 0"/>
          <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                width={240}
                                margin="8px 0 8px 0"/>
        </div>
      </div>
    </div>;

export default class ImportMethod extends Component<Props, State> {
  state = {
    modalOpen: {
      DIY: false,
      CLASSIC: false,
    },
  };

  handleOpenModal = (e: MouseEvent, forecastType: ForecastType) => {
    e.stopPropagation();
    this.setState({ modalOpen: { [forecastType]: true } });
  }

  handleCloseModal = (forecastType: ForecastType) => {
    this.setState({ modalOpen: { [forecastType]: false } });
  }

  buildOption(titleIntl: string, subtitleIntl: string, descriptionIntl: string, forecastType: ForecastType,
              url: string, importMethod?: ForecastType, items: (ShoppingCartItem | BagItem)[], percentageOff?: string) {
    const { handleChangeOption, forecast, isLoading } = this.props;
    const { modalOpen } = this.state;
    let fromPrice;
    if (!!forecast && !!forecast[forecastType] && forecast[forecastType].length === 0) {
      return null;
    }
    if (!!forecast && !!forecast[forecastType] && forecast[forecastType].length) {
      if (forecastType === 'DIY') {
        const couriers = [];
        for (let c of forecast['DIY']) {
          if (DIYCourier.shouldIncludeCourier(c, items)) couriers.push(c);
        }
        if (couriers.length) {
          fromPrice = Math.min(...couriers
            .map(fy => parseFloat(fy.importationFee) +
              parseFloat(fy.customsDuties) +
              parseFloat(fy.qempo) +
              parseFloat(fy.lastMileShipping)));
        }
      }
      
      if (!fromPrice) {
        fromPrice = Math.min(...(forecast[forecastType]: any[])
          .map(fy => parseFloat(fy.importationFee) +
            parseFloat(fy.customsDuties) +
            parseFloat(fy.qempo) +
            parseFloat(fy.lastMileShipping)));
      }
    }
    const active = forecastType === importMethod;
    
    const titleWrap = (
      <div className={style.titleWrap}>
        <img src={forecastType === 'CLASSIC' ? full : lite} alt="box"/>
        <div className={style.textWrap}>
          <FormattedMessage id={titleIntl}>{
            txt => <p className={style.purchaseMethod}>{txt}</p>
          }</FormattedMessage>
          <FormattedMessage id={subtitleIntl}>{
            txt => <p className={style.purchaseMethodSubtitle}>{txt}</p>
          }</FormattedMessage>
        </div>
      </div>
    );

    return (
      <div className={classNames(style.option, { [style.active]: active }, { [style.loading]: isLoading })}
           onClick={e => {
             e.stopPropagation();
             handleChangeOption(forecastType);
           }}>
        <div className={style.radioWrap}>
          <RadioButton checked={importMethod === forecastType}
                       name="importMethod"/>
        </div>
        <div className={classNames(style.titleWrapResponsive, style.mobile)}>
          {titleWrap}
        </div>
        <div className={style.mainContent}>
          <div className={style.leftWrap}>
            <div className={classNames(style.titleWrapResponsive, style.web)}>
              {titleWrap}
            </div>
            <FormattedMessage id={descriptionIntl}>{
              txt => <p className={style.text}>{txt}</p>
            }</FormattedMessage>
          </div>
          <div className={style.vr} />
          <div className={style.rightWrap}>
            <div className={style.content}>
              <FormattedMessage id="newOrder.quote.importMethod.from">{
                txt => <p className={style.from}>{txt}</p>
              }</FormattedMessage>
              <p className={style.price}>$ <CommaFormat price={fromPrice || ''}/></p>
              <div className={style.howItWorksWrap}>
                <FormattedMessage id="newOrder.quote.importMethod.howItWorks">{
                  txt => <button onClick={(e) => this.handleOpenModal(e, forecastType)}
                                 className={classNames(buttonStyle.btn, style.howItWorksBtn)}
                  >
                    <i className={icons.symbolPlay}/>
                    {txt}
                  </button>
                }</FormattedMessage>
                <VideoModal 
                  handleClose={(e: MouseEvent) => {
                    e.stopPropagation();
                    this.handleCloseModal(forecastType);
                  }}
                  modalVisible={modalOpen[forecastType] || false}
                  url={importVideoUrl[forecastType]}/>
              </div>
            </div>
          </div>
        </div>
        {/*<div className={style.topWrap}
             onClick={_ => handleChangeOption(forecastType)}>
          <div className={style.radioWrap}>
            <FormattedMessage id={titleIntl}>{
              txt => <p className={style.purchaseMethod}>{txt}</p>
            }</FormattedMessage>
            <FormattedMessage id={subtitleIntl}>{
              txt => <p className={style.purchaseMethodSubtitle}>{txt}</p>
            }</FormattedMessage>
            <RadioButton checked={importMethod === forecastType}
                         name="importMethod"/>
          </div>
          <FormattedMessage id="newOrder.quote.importMethod.from">{
            txt => <p className={classNames(style.from, { [style.active]: active })}>{txt} $ <CommaFormat
              price={fromPrice}/> <span>{percentageOff}</span></p>
          }</FormattedMessage>
          <FormattedMessage id={descriptionIntl}>{
            txt => <p className={style.description}>{txt}</p>
          }</FormattedMessage>
        </div>
        <hr/>
        <div className={style.bottomWrap}>
          <FormattedMessage id="newOrder.quote.importMethod.howItWorks">{
            txt => <button onClick={() => this.handleOpenModal(forecastType)}
                           className={classNames(buttonStyle.btn, style.howItWorksBtn)}
            >
              <i className={icons.symbolPlay}/>
              {txt}
            </button>
          }</FormattedMessage>
          <VideoModal handleClose={() => this.handleCloseModal(forecastType)}
                      modalVisible={modalOpen[forecastType]}
                      url={importVideoUrl[forecastType]}/>
        </div>*/}
      </div>
    );
  }

  getServicesPrices = (forecast?: Forecast, types: string[]): { [key: string]: number } => {
    let pricesMap = {};
    types.forEach((forecastType: string) => {
      if (!!forecast && forecast[forecastType].length !== 0) {
        pricesMap[forecastType] = Math.min(...(forecast[forecastType]: any[])
          .map(fy => parseFloat(fy.importationFee) +
            parseFloat(fy.customsDuties) +
            parseFloat(fy.qempo) +
            parseFloat(fy.lastMileShipping))
        )
      }
    });
    return pricesMap;
  };

  getPercentageOffText = (servicesMap: { [key: string]: number }): string => {
    if (Object.keys(servicesMap).length === 0 ||
      (!servicesMap['DIY'] || !servicesMap['CLASSIC'])) return '';
    const percentage: number = Math.round(servicesMap['DIY'] * 100 / servicesMap['CLASSIC']) - 100;
    if (percentage < 0) {
      return `(${percentage}% OFF)`;
    } else return '';
  };
  
  shouldShowClassic = (items: (ShoppingCartItem | BagItem)[]): boolean => {
    return true;
    // return !items.some((i) => isIphone13(i.url, i.name));
  };

  render() {
    const { forecast, importMethod, preferredImportationMethod, items } = this.props;
    const servicesMap = this.getServicesPrices(forecast, ['DIY', 'CLASSIC']);
    const percentageOff = this.getPercentageOffText(servicesMap);

    return (
      <div className={classNames(style.optionsWrap)}>
        {!forecast && <Fragment>
          <ImportMethodOptionLoading url="#"/>
          {!(preferredImportationMethod === 'full' || preferredImportationMethod === 'lite')
          && <ImportMethodOptionLoading url="#"/>}
        </Fragment>}
        {!!forecast &&
        <Fragment>
          {preferredImportationMethod !== 'full' &&
          this.buildOption(
            'newOrder.quote.importMethod.diy.title',
            'newOrder.quote.importMethod.diy.subtitle',
            'newOrder.quote.importMethod.diy.description',
            'DIY',
            '#',
            importMethod,
            items,
            percentageOff,
          )}
          {preferredImportationMethod !== 'lite' && 
          this.shouldShowClassic(items) && 
          this.buildOption(
            'newOrder.quote.importMethod.classic.title',
            'newOrder.quote.importMethod.classic.subtitle',
            'newOrder.quote.importMethod.classic.description',
            'CLASSIC',
            '#',
            importMethod,
            items,
          )}
        </Fragment>}
      </div>
    );
  }
}

ImportMethod.propTypes = {
  forecast: PropTypes.object,
  isLoading: PropTypes.bool,
  handleChangeOption: PropTypes.func.isRequired,
  importMethod: PropTypes.string,
  preferredImportationMethod: PropTypes.string,
  items: PropTypes.array.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/QuoteStep/components/ImportMethod/ImportMethod.jsx